import { useDeleteRecordingMutation } from '@carboncopyco/data';
import { DeleteOutlineIcon } from '@carboncopyco/icons';
import type { Folder, IRecording } from '@carboncopyco/types';
import { ConfirmAlert, notify } from '@carboncopyco/ui';
import { useCloneAnalytics } from '@carboncopyco/utils';
import { useBoolean } from 'ahooks';

interface ArchiveCloneModalProps {
  organizationId: string;
  cloneId: IRecording['id'];
  onClose: () => void;
  show: boolean;
  navigateOnSuccess?: () => void;
  folderId?: Folder['id'];
}

export const ArchiveCloneModal = ({
  organizationId,
  cloneId,
  show,
  onClose,
  navigateOnSuccess,
  folderId,
}: ArchiveCloneModalProps) => {
  const [archiveClone, { isLoading }] = useDeleteRecordingMutation();
  const [isDisabled, setIsDisabled] = useBoolean();
  const { trackCloneClickArchiveApprove, trackCloneClickArchiveCancel } =
    useCloneAnalytics({
      location: 'archive_clone_modal',
      cloneId: cloneId,
    });

  return (
    <ConfirmAlert
      disabled={isDisabled}
      loading={isLoading}
      title="Archive clone"
      description="This clone will no longer be accessible unless it’s restored"
      show={show}
      onConfirm={() => {
        trackCloneClickArchiveApprove();
        setIsDisabled.setTrue();
        archiveClone({
          id: cloneId,
          organizationId,
          folderId,
        })
          .unwrap()
          .then(() => {
            navigateOnSuccess?.();
            notify.success('Archived clone successfully');
          })
          .catch((error) => {
            const errorMessage = error?.data?.message;
            if (errorMessage.includes('Playbook')) {
              notify.error(error.data.message);
              return;
            }
            notify.error('Error archiving clone');
          })
          .finally(() => {
            setIsDisabled.setFalse();
            onClose();
          });
      }}
      onClose={() => {
        trackCloneClickArchiveCancel();
        onClose();
      }}
      danger
      containerClass="w-ds-normal-modal"
      yesIcon={<DeleteOutlineIcon />}
      yesLabel="Archive"
    />
  );
};
